import { Flex, Text } from '@chakra-ui/react'
import { createRef, useEffect, useState } from 'react'
import ReactPlayer from 'react-player'
import { useParams } from 'react-router-dom'

import './VideoQuiz.css'
import { Icon } from '../../components/Icon'
import { CurrentStepDoneResponseQuestion } from '../../services/VideoQuiz/types'
import { VideoQuizService } from '../../services/VideoQuiz/VideoQuiz.service'
import { magenta } from '../../theme'
import { Done } from './Done'
import { Loading } from './Loading'

export function VideoQuiz() {
  const playerRef = createRef<ReactPlayer>()
  const params = useParams()
  const videoQuizService = new VideoQuizService()
  const [loading, setLoading] = useState<boolean>(true)
  const [videoUrl, setVideoUrl] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [imageUrl, setImageUrl] = useState<string>('')
  const [questions, setQuestions] = useState<CurrentStepDoneResponseQuestion[]>([])
  const [question, setQuestion] = useState<string>('')
  const [answers, setAnswers] = useState<{ id: number; answer: string }[]>([])
  const [play, setPlay] = useState<boolean>(false)
  const [videoProgress, setVideoProgress] = useState<number>(0)
  const [videoLength, setVideoLength] = useState<number>(0)
  const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0)
  const [isSeeking, setIsSeeking] = useState<boolean>(false)
  const [isFinished, setIsFinished] = useState<boolean>(false)
  const [type, setType] = useState<'video' | 'image'>('video')
  const [availableHeight, setAvailableHeight] = useState<number>(0)
  const [done, setDone] = useState<boolean>(false)
  const [isFirstVideo, setIsFirstVideo] = useState<boolean>(true)
  const [isLastVideo, setIsLastVideo] = useState<boolean>(false)

  useEffect(() => {
    setAvailableHeight(window.innerHeight)

    loadStepInfo()
  }, [])

  const loadStepInfo = async () => {
    if (!params.uuid) return

    const currentStep = await videoQuizService.getStepInfo(params.uuid)

    setDone(currentStep.finished)
    setIsLastVideo(currentStep.is_last_video)

    if (currentStep.finished) {
      setLoading(false)
      return
    }

    setType('video')
    setVideoProgress(0)
    setVideoCurrentTime(0)
    setPlay(true)
    setVideoUrl(currentStep.video)
    setQuestions(currentStep.questions)

    if (!isFirstVideo) {
      setPlay(true)
    }
    setIsFirstVideo(false)
    setLoading(false)
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, '0')
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0')

    return `${minutes}:${seconds}`
  }

  const setSeek = (time: number) => {
    setVideoProgress((time / videoLength) * 100)
    setIsFinished(false)
    playerRef.current?.seekTo(time)
  }

  const makeAnswer = async (answer: number) => {
    if (!params.uuid) return

    setPlay(false)
    setIsFinished(false)

    const response = await videoQuizService.answer(params.uuid, questions[0].id, answer)

    setQuestion('')
    setAnswers([])
    setQuestions(questions.slice(1))

    setVideoUrl(response.image)
    setType('video')
    setPlay(true)
  }

  const showLastImage = () => {
    setIsFinished(false)
    setPlay(true)
    setVideoUrl('https://painel.abra.com.br/video-quiz/06.mp4')
    setType('video')
  }

  const loadNextStep = () => {
    if (questions.length === 0) {
      if (isLastVideo) {
        return showLastImage()
      }

      setIsFinished(false)
      return loadStepInfo()
    }

    const currentQuestion = questions[0]
    setQuestion(currentQuestion.question)
    setAnswers(currentQuestion.answers.sort(() => Math.random() - 0.5))
    setIsFinished(true)
    setType('video')
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : done ? (
        <Done />
      ) : (
        <>
          <div style={{ display: type == 'video' ? 'block' : 'none' }}>
            <div
              className={'seek-bar' + (isSeeking ? ' seeking' : '')}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                const x = e.clientX - rect.left
                const seek = (x / rect.width) * videoLength
                setSeek(seek)
              }}
              onTouchMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                const x = e.touches[0].clientX - rect.left
                const seek = (x / rect.width) * videoLength
                setIsSeeking(true)
                setSeek(seek)
              }}
              onTouchEnd={() => {
                setIsSeeking(false)
              }}
            >
              <div className="seek-bar-progress" style={{ width: `${videoProgress}%` }} />
            </div>

            <div className={'progress-time' + (isSeeking ? ' seeking' : '')}>
              {formatTime(videoCurrentTime)} / {formatTime(videoLength)}
            </div>

            {!play && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '96px',
                  cursor: 'pointer'
                }}
                id="play-button"
                onClick={() => setPlay(true)}
              >
                <Icon name="lni lni-play" color="magenta.500" textShadow={`0 0 10px ${magenta[500]}`} />
              </div>
            )}

            <Flex
              justifyContent="center"
              alignItems="center"
              height={`${availableHeight}px`}
              backgroundColor="blackAlpha.900"
            >
              <div onClick={() => setPlay(!play)} style={{ opacity: play ? 1 : 0.3 }}>
                <ReactPlayer
                  ref={playerRef}
                  url={videoUrl}
                  playing={play}
                  controls={false}
                  width="100%"
                  height="auto"
                  playsinline={true}
                  onProgress={(progress) => {
                    setVideoProgress(progress.played * 100)
                    setVideoCurrentTime(progress.playedSeconds)
                    setIsFinished(progress.played === 1)

                    if (progress.played === 1) {
                      loadNextStep()
                    }
                  }}
                  onDuration={(duration) => {
                    setVideoLength(duration)
                  }}
                />
              </div>
            </Flex>
          </div>

          <div style={{ display: type == 'image' ? 'block' : 'none' }}>
            <Flex
              justifyContent="center"
              alignItems="center"
              height="100vh"
              backgroundColor="blackAlpha.900"
              onClick={() => loadNextStep()}
            >
              <img src={imageUrl} alt="Imagem da etapa do quiz" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            </Flex>
          </div>

          {isFinished && (
            <Flex
              direction="column"
              height="100vh"
              color="white"
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              pt="20px"
              justifyContent="end"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(5px)' }}
            >
              <Text className="question" marginBlockEnd="20px" fontSize="lg">
                {question}
              </Text>
              <div className="answers">
                {answers.map((answer, index) => (
                  <Flex
                    key={index}
                    className="answer"
                    align="center"
                    onClick={() => {
                      makeAnswer(answer.id)
                    }}
                  >
                    <div className="alternative">{String.fromCharCode(65 + index)}</div>
                    {answer.answer}
                  </Flex>
                ))}
              </div>
            </Flex>
          )}
        </>
      )}
    </>
  )
}
